import React, { useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { Calendar as CalendarIcon, Clock, X } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Modern accessible date + optional time picker.
 * Emits an ISO string on change ("2026-01-15" for date-only, "2026-01-15T09:30" with time).
 *
 * Props:
 *   value        — ISO string or empty
 *   onChange     — (isoString) => void
 *   showTime     — boolean, adds a small time input
 *   minDate      — optional Date
 *   placeholder  — default "Pick a date"
 *   variant      — 'admin' | 'public' — tunes surface colors
 */
export default function DatePicker({
  value,
  onChange,
  showTime = false,
  minDate,
  placeholder = 'Pick a date',
  variant = 'admin',
  disabled,
  testId,
}) {
  const [open, setOpen] = useState(false);

  const parsed = React.useMemo(() => {
    if (!value) return { date: undefined, time: '' };
    const iso = value.includes('T') ? value : `${value}T00:00`;
    const d = new Date(iso);
    if (!isValid(d)) return { date: undefined, time: '' };
    const time = value.includes('T') ? format(d, 'HH:mm') : '';
    return { date: d, time };
  }, [value]);

  const commit = (nextDate, nextTime) => {
    if (!nextDate) { onChange?.(''); return; }
    const dPart = format(nextDate, 'yyyy-MM-dd');
    if (showTime && nextTime) onChange?.(`${dPart}T${nextTime}`);
    else onChange?.(dPart);
  };

  const triggerCls = variant === 'public'
    ? 'w-full inline-flex items-center gap-2 border border-black/15 bg-white text-[#141414] rounded-xl px-3.5 py-3 text-sm hover:border-[#0F2E23] focus:outline-none focus:ring-2 focus:ring-[#0F2E23]/25 transition-colors'
    : 'w-full inline-flex items-center gap-2 border border-white/10 bg-black/30 text-white rounded-xl px-3 py-2.5 text-sm hover:border-[var(--pg-admin-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--pg-admin-accent-ring)] transition-colors';

  const label = parsed.date ? (showTime && parsed.time ? `${format(parsed.date, 'EEE, MMM d, yyyy')} · ${parsed.time}` : format(parsed.date, 'EEE, MMM d, yyyy')) : '';

  return (
    <div className="grid gap-2" data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} className={triggerCls} data-testid={testId ? `${testId}-trigger` : undefined}>
            <CalendarIcon className="w-4 h-4 opacity-70" />
            {label ? (
              <span className="flex-1 text-left truncate">{label}</span>
            ) : (
              <span className={`flex-1 text-left truncate ${variant === 'public' ? 'text-[#141414]/40' : 'text-white/40'}`}>{placeholder}</span>
            )}
            {label && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); commit(undefined, ''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); commit(undefined, ''); } }}
                className={`p-0.5 rounded hover:bg-white/10 ${variant === 'public' ? 'hover:bg-black/5' : ''}`}
                aria-label="Clear date"
              >
                <X className="w-3.5 h-3.5 opacity-60" />
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-2 rounded-2xl shadow-xl">
          <Calendar
            mode="single"
            selected={parsed.date}
            onSelect={(d) => { commit(d, parsed.time); setOpen(false); }}
            initialFocus
            disabled={minDate ? { before: minDate } : undefined}
          />
          {showTime && (
            <div className="flex items-center gap-2 border-t border-black/10 dark:border-white/10 px-3 py-2 mt-1">
              <Clock className="w-4 h-4 opacity-60" />
              <input
                type="time"
                value={parsed.time || '09:00'}
                onChange={(e) => commit(parsed.date, e.target.value)}
                className="flex-1 rounded-lg border border-black/10 dark:border-white/10 bg-transparent px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[var(--pg-admin-accent-ring)]"
              />
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
